/**
 * UnifiedOnboarding — guided campaign wizard
 * add-site → profile → select-goal → campaign → install-script → dashboard
 *
 * Value before install (founder decision 2026-09-10). Of 218 real signups
 * between 2026-07-11 and 2026-09-08, 135 never added a property and none of
 * those started a trial; of the 83 who did, 51 trialed and 28 subscribed.
 * Research only needs the URL, not the tracker, so the drafted tests land
 * before the install and become the reason to install.
 *
 * URL first (design spec v2, 2026-09-10): the crawl fires on site submit and
 * runs while You and Goal are answered, so it costs the user nothing. There is
 * no `research` screen any more: the plan renders immediately with heuristic
 * drafts and a "Still reading" strip, and upgrades in place when the job lands.
 * `/onboarding/research` redirects to `/onboarding/campaign`.
 */

import { useState, useEffect, useCallback, useRef, useMemo, type CSSProperties } from "react";
import { useNavigate, useLocation, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { useAuth } from "@/lib/contexts/AuthContext";
import { useTheme } from "@/lib/contexts/ThemeContext";
import { useSubscription } from "@/lib/contexts/SubscriptionContext";
import { saveWorkMode } from "@/lib/api/billing";
import { WORK_MODE_KEY, readWorkMode } from "@/lib/utils/onboardingResume";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { normalizeDomain } from "@/lib/utils/domainUtils";

import { getAllWorkspaces, createWorkspace } from "@/lib/api/workspaces";
import {
  addCustomSiteToWorkspace,
  getAllProperties,
  getWorkspaceOwnerScriptId,
  trackSplitTestSetup,
  sendMobileSetupLink,
  getUserFacingApiError,
  checkSite,
} from "@/lib/api/api";
import { pickScriptId, scriptIdFromOwnerResponse } from "@/lib/utils/trackingSnippet";
import { screenshotStateFor, triggerExperimentGeneration } from "@/lib/api/onboardingTests";
import { track } from "@/lib/utils/track";
import { hasConfirmedOtp } from "@/lib/utils/authState";
import { getAcquisitionContext, getSignupPath } from "@/lib/utils/acquisitionContext";
import { icpFromLandingFit } from "@/lib/utils/landingFit";
import { InstallScriptPanel } from "@/components/onboarding/InstallScriptPanel";
import { OnboardingStepper, formatAgo, type OnboardingStepperStep } from "@/components/onboarding/OnboardingStepper";
import { OnboardingExampleTests } from "@/components/onboarding/OnboardingExampleTests";
import {
  OnboardingEvidence,
  type OnboardingEvidenceStep,
} from "@/components/onboarding/OnboardingEvidence";
import { OnboardingChoiceCard } from "@/components/onboarding/OnboardingChoiceCard";
import { OnboardingPlanRecs, type PlanCtaMeta } from "@/components/onboarding/OnboardingPlanRecs";
import { OnboardingCallModal } from "@/components/onboarding/OnboardingCallModal";
import { OnboardingReadingSite, readingSteps } from "@/components/onboarding/OnboardingReadingSite";
import { useOnboardingSnapshot } from "@/components/onboarding/useOnboardingSnapshot";
import { OnboardingRevenueStep, type RevenueConnectorId } from "@/components/onboarding/OnboardingRevenueStep";
import { OnboardingAgentStep } from "@/components/onboarding/OnboardingAgentStep";
import { useRevenueConnections } from "@/components/onboarding/useRevenueConnections";
import { initiateGoogleAdsOAuth, initiateMetaOAuth } from "@/lib/api/oauth";
import { logConnectorEvent, reportConnectorFailure } from "@/lib/observability/connectors";
import { generateSlugId } from "@/lib/routing/slug-id";
import {
  goalNoun,
  ONBOARDING_GOAL_DESCRIPTIONS,
  ONBOARDING_GOAL_LABELS,
  ONBOARDING_ICP_DESCRIPTIONS,
  ONBOARDING_ICP_LABELS,
  SELECTABLE_ONBOARDING_GOALS,
  isOnboardingIcpId,
  isSelectableOnboardingGoalId,
  type OnboardingGoalId,
  type OnboardingIcpId,
} from "@/lib/types/onboarding-goal";
import { getSiteContext, setSiteContext, setSiteContextPlatform } from "@/lib/utils/siteContext";
import { readOnboardingDraft, writeOnboardingDraft } from "@/lib/utils/onboardingDraft";
import { markGenerationVerified, readGenerationPending, stampGenerationPending } from "@/lib/utils/generationPending";
import { heuristicRecommendations } from "@/lib/config/onboardingHeuristics";
import { useExperimentGeneration } from "@/hooks/useExperimentGeneration";
import type { PlatformId } from "@/lib/types/platform";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupText } from "@/components/ui/input-group";
import { Label } from "@/components/ui/label";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Loading01Icon,
  ArrowRight01Icon,
  Tick01Icon,
  Alert01Icon,
  FileAttachmentIcon,
  Comment02Icon,
  ArrowDown01Icon,
  Mail01Icon,
  SmartPhone01Icon,
  Cancel01Icon,
} from "@hugeicons/core-free-icons";
import artTeam from "@/assets/hero/hero-icon-team.png";
import artYou from "@/assets/hero/hero-icon-you.png";
import artAgencies from "@/assets/hero/hero-icon-agencies.png";
import artCreators from "@/assets/hero/hero-icon-creators.png";
import artSaas from "@/assets/hero/hero-icon-saas.png";
import artRevenue from "@/assets/hero/hero-icon-revenue.png";
import artSubscribers from "@/assets/hero/hero-icon-subscribers.png";
import artAnalytics from "@/assets/hero/hero-icon-analytics.png";
import { loadIntercom, showIntercom } from "@/lib/utils/intercom";

import HumblyticsLogo from "@/assets/humblytics-web-app-logo.svg";
import { OnboardingWorkModeChoice, type OnboardingWorkMode } from "@/components/onboarding/OnboardingWorkModeChoice";

type OnboardingStep =
  | "start"
  | "add-site"
  | "profile"
  | "connect-agent"
  | "select-goal"
  | "campaign"
  | "install-script";

/**
 * Ceiling on the plan's "Still reading" strip, not a blocking screen: past it
 * the crawl is called failed and the heuristic drafts stand as they are.
 */
const RESEARCH_CAP_MS = 20_000;

/** Glossy product icons for the choice rows: the site's own pack plus four generated in its register. */
const GOAL_ART: Record<Exclude<OnboardingGoalId, "express">, string> = {
  saas: artSaas,
  ecommerce: artRevenue,
  newsletter: artSubscribers,
  agency: artAnalytics,
};

const ICP_ART: Record<OnboardingIcpId, string> = {
  marketing_team: artTeam,
  founder: artYou,
  agency: artAgencies,
  creator: artCreators,
};

const ICP_IDS: OnboardingIcpId[] = ["marketing_team", "founder", "agency", "creator"];

/** One question and one line per step. Sourced numbers live on the plan (examples) and the tracker only. */
const STEP_META: Record<OnboardingStep, { title: string; description: string }> = {
  start: {
    title: "How do you want to use Humblytics?",
    description: "Pick one. It sets your plan and what you see. You can switch later in Settings.",
  },
  "add-site": {
    title: "What's your website?",
    description: "We read it and draft your first tests. No code yet.",
  },
  profile: {
    title: "Who are you?",
    description: "Pick the closest. It shapes the tests we draft.",
  },
  "connect-agent": {
    title: "Connect your agent",
    description: "Then it sets up your site and the tracking script.",
  },
  "select-goal": {
    title: "What do you want more of?",
    description: "Pick one. The first tests are built around it.",
  },
  campaign: {
    title: "Your first tests",
    description: "Drafted from your page. Scored in Stripe revenue, not clicks.",
  },
  "install-script": {
    title: "Add the script to go live",
    description: "One tag on your site. Tests run once it's on.",
  },
};

/** The sourced line under a step description. Tracker only. */
const STEP_EVIDENCE: Partial<Record<OnboardingStep, OnboardingEvidenceStep>> = {
  "install-script": "install-script",
};

const MOBILE_BANNER_DISMISSED_KEY = "humblytics:onboarding:mobile-banner-dismissed";

const PATH_TO_STEP: Record<string, OnboardingStep> = {
  "/onboarding": "start",
  "/onboarding/start": "start",
  "/onboarding/add-site": "add-site",
  "/onboarding/profile": "profile",
  "/onboarding/connect-agent": "connect-agent",
  "/onboarding/select-goal": "select-goal",
  "/onboarding/campaign": "campaign",
  "/onboarding/install-script": "install-script",
};
const STEP_TO_PATH: Record<OnboardingStep, string> = {
  start: "/onboarding/start",
  "add-site": "/onboarding/add-site",
  profile: "/onboarding/profile",
  "connect-agent": "/onboarding/connect-agent",
  "select-goal": "/onboarding/select-goal",
  campaign: "/onboarding/campaign",
  "install-script": "/onboarding/install-script",
};

/** The retired research screen. Kept as a redirect so saved links still work. */
const RESEARCH_PATH = "/onboarding/research";

/** Flow order. Drives the rail, the dev jumper and the funnel so they cannot drift. */
const STEP_ORDER: OnboardingStep[] = [
  "start",
  "add-site",
  "profile",
  "select-goal",
  "campaign",
  "install-script",
];

/**
 * Agent path: the first answer is "with my agent", so the next and last web
 * step is connecting it. The agent adds the site, installs the script and
 * picks the first test through MCP; the person lands on /agent.
 */
const AGENT_STEP_ORDER: OnboardingStep[] = ["start", "connect-agent"];

/** Where the agent path ends: account management for agent-plan accounts. */
const AGENT_HOME_PATH = "/agent";


const STEP_SHORT_LABELS: Record<OnboardingStep, string> = {
  start: "Start",
  "add-site": "Website",
  profile: "You",
  "connect-agent": "Agent",
  "select-goal": "Goal",
  campaign: "Plan",
  "install-script": "Tracker",
};


/** Steps that cannot render without a property. */
const REQUIRES_PROPERTY: OnboardingStep[] = [
  "profile",
  "select-goal",
  "campaign",
  "install-script",
];

type InitialWizardContext = {
  icp: OnboardingIcpId | null;
  goal: OnboardingGoalId | null;
  platform: PlatformId | null;
};

/**
 * Rehydrate the pre-property wizard answers once on mount. The sessionStorage
 * draft only survives within the same tab, so a new tab, a mobile "send me a
 * link" handoff, or a bookmarked deep-link would otherwise lose the goal the
 * user picked and drop the heuristic fallback back to generic SaaS tests. When
 * a propertyId is present, fall back to the durable per-property site context
 * (localStorage) and the generation-pending record.
 */
function readInitialWizardContext(pid: string | null): InitialWizardContext {
  const draft = readOnboardingDraft();
  const ctx = getSiteContext(pid);
  const pending = readGenerationPending();
  const pendingGoal =
    pending && (!pid || pending.propertyId === pid) ? pending.goal : null;

  const icp = draft.icp ?? ctx?.icp ?? icpFromLandingFit(getAcquisitionContext().fit) ?? null;
  const goal = draft.goal ?? ctx?.goal ?? pendingGoal ?? null;
  const platform = ctx?.platform ?? null;

  return {
    icp: isOnboardingIcpId(icp) ? icp : null,
    goal: isSelectableOnboardingGoalId(goal) ? goal : null,
    platform,
  };
}

export default function UnifiedOnboarding() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { token, user } = useAuth();
  const { setTheme } = useTheme();
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (searchParams.get("stripe_session_id") && (hasConfirmedOtp() || token)) {
      navigate(`/dashboard${location.search}`, { replace: true });
    }
  }, [searchParams, location.search, navigate, token]);

  /**
   * `/onboarding/research` is retired (v2): the plan renders immediately and
   * upgrades in place. Redirect rather than 404, preserving `propertyId` (and
   * anything else on the query) so saved links and emails still resolve.
   */
  useEffect(() => {
    if (location.pathname !== RESEARCH_PATH) return;
    const qs = new URLSearchParams(location.search).toString();
    navigate(qs ? `${STEP_TO_PATH.campaign}?${qs}` : STEP_TO_PATH.campaign, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const stepFromPath = PATH_TO_STEP[location.pathname] ?? "add-site";
  const [step, setStepState] = useState<OnboardingStep>(stepFromPath);

  useEffect(() => {
    if (location.pathname === RESEARCH_PATH) return;
    const s = PATH_TO_STEP[location.pathname] ?? "add-site";
    if (s !== step) setStepState(s);
  }, [location.pathname, step]);

  // Read the durable wizard context exactly once (lazy initializer), not on
  // every URL change — the values only seed initial state.
  const [initialContext] = useState<InitialWizardContext>(() =>
    readInitialWizardContext(searchParams.get("propertyId")),
  );

  const [selectedIcp, setSelectedIcp] = useState<OnboardingIcpId | null>(
    initialContext.icp,
  );
  const [selectedGoal, setSelectedGoal] = useState<OnboardingGoalId | null>(
    initialContext.goal,
  );
  /** Step 02's first answer: agent or app. Picks the plan type and the step order. */
  const [workMode, setWorkMode] = useState<OnboardingWorkMode | null>(readWorkMode);
  // The agent path is only start → connect-agent. The saved choice is per
  // browser, so on any app step (an existing account sent to add-site, a
  // teammate on the same machine) it must not switch the app flow's branches.
  const isAgentPath = workMode === "agent" && AGENT_STEP_ORDER.includes(step);
  const stepOrder = isAgentPath ? AGENT_STEP_ORDER : STEP_ORDER;
  const [detectedPlatform, setDetectedPlatform] = useState<PlatformId | null>(
    initialContext.platform,
  );
  const [callOpen, setCallOpen] = useState(false);

  // Track each step view once. A ref (not state) holds the guard so the
  // side-effecting track() calls live in the effect body — putting them inside
  // a setState updater double-fires them under StrictMode / re-renders.
  const trackedStepsRef = useRef<Set<OnboardingStep>>(new Set());
  useEffect(() => {
    if (!token) return;
    const tracked = trackedStepsRef.current;
    if (tracked.has(step)) return;
    track("onboarding_step_viewed", token, { step });
    // Same firing point as before: the first wizard step this session shows.
    // That step is `add-site` since the v2 reorder, not `profile`.
    if (tracked.size === 0) {
      track("onboarding_started", token);
    }
    tracked.add(step);
  }, [step, token]);

  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const isMobile = useMemo(() => /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent), []);
  const [mobileLinkSent, setMobileLinkSent] = useState(false);
  const [isSendingMobileLink, setIsSendingMobileLink] = useState(false);
  const [mobileBannerDismissed, setMobileBannerDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(MOBILE_BANNER_DISMISSED_KEY) === "1";
    } catch {
      return false;
    }
  });

  const [marketingDomain] = useState<string>(() => {
    const raw = document.cookie
      .split("; ")
      .find((c) => c.startsWith("humblytics_signup_domain="))
      ?.split("=")[1];
    if (raw) {
      document.cookie = "humblytics_signup_domain=; max-age=0; path=/; domain=.humblytics.com; SameSite=Lax";
      document.cookie = "humblytics_signup_domain=; max-age=0; path=/";
      return decodeURIComponent(raw);
    }
    return "";
  });

  const [domain, setDomain] = useState(() => searchParams.get("domain") || marketingDomain || "");
  const [siteName, setSiteName] = useState("");
  const [domainError, setDomainError] = useState("");
  const [isCreatingSite, setIsCreatingSite] = useState(false);
  /** The domain whose pre-add check failed, and what it answered (null = nothing). */
  const [unreachableSite, setUnreachableSite] = useState<{ domain: string; status: number | null } | null>(null);
  const [propertyId, setPropertyId] = useState<string | null>(() => searchParams.get("propertyId"));
  /** The domain `propertyId` was created for, so editing the site step can tell a change from a revisit. */
  const [createdDomain, setCreatedDomain] = useState<string | null>(() =>
    searchParams.get("propertyId") ? normalizeDomain(searchParams.get("domain") ?? "") : null,
  );
  const [scriptId, setScriptId] = useState<string | null>(null);
  const [scriptIdLoadFailed, setScriptIdLoadFailed] = useState(false);
  const [workspaceLoadFailed, setWorkspaceLoadFailed] = useState(false);
  /** Bumped by the site step's Retry to re-run the workspace bootstrap. */
  const [workspaceAttempt, setWorkspaceAttempt] = useState(0);
  const [verified, setVerified] = useState(false);
  /** ISO stamp of the first pageview, i.e. the moment verification passed. */
  const [firstHitAt, setFirstHitAt] = useState<string | null>(null);
  const [agoTick, setAgoTick] = useState(() => Date.now());
  useEffect(() => {
    if (!firstHitAt) return;
    const id = window.setInterval(() => setAgoTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [firstHitAt]);

  // Mount-time ?propertyId= (Hub resume). Held in a ref so the workspace
  // bootstrap can resolve the owning workspace without taking propertyId as a
  // dep — otherwise the first-pass add-site flow (which sets propertyId) would
  // re-run bootstrap and, if a reload returned empty, create a duplicate
  // workspace or cancel a valid owner-script-id lookup.
  const resumePropertyIdRef = useRef<string | null>(searchParams.get("propertyId"));

  const setStep = useCallback(
    (
      s: OnboardingStep,
      overrides?: { propertyId?: string; domain?: string; replace?: boolean },
    ) => {
      const params = new URLSearchParams();
      const pid = overrides?.propertyId ?? propertyId;
      const d = overrides?.domain ?? domain;
      if (pid) params.set("propertyId", pid);
      if (d) params.set("domain", d);
      const qs = params.toString();
      navigate(qs ? `${STEP_TO_PATH[s]}?${qs}` : STEP_TO_PATH[s], {
        replace: overrides?.replace ?? false,
      });
    },
    [navigate, propertyId, domain],
  );

  const [devPanelOpen, setDevPanelOpen] = useState(false);
  /** Set once the tracker step resolves, so the exit CTA replaces the panel. */
  const [installOutcome, setInstallOutcome] = useState<"verified" | "skipped" | null>(null);
  /**
   * The agent step (Paper "08 - API vs Platform · v2", 05/06). Optional and
   * last: tracker → connectors → agent → /dashboard. It lives on the tracker
   * route like the connectors view, so it needs no route of its own.
   */
  const [agentOpen, setAgentOpen] = useState(false);
  const stepRef = useRef(step);
  useEffect(() => {
    stepRef.current = step;
  }, [step]);
  // The tracker's onVerified holds the "Tracking is live" beat for 1s before
  // the exit card paints. Only the PAINT waits on this timer: the verified
  // state, the pending-generation stamp and `onboarding_completed` are recorded
  // the moment verification lands (see `recordInstall`). Leaving the step or
  // unmounting cancels the paint, never the record. Bugbot findings on PR #194
  // (exit card over another step; timer cancel dropping the completion).
  const verifyTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (step !== "install-script" && verifyTimerRef.current !== null) {
      window.clearTimeout(verifyTimerRef.current);
      verifyTimerRef.current = null;
    }
  }, [step]);
  useEffect(
    () => () => {
      if (verifyTimerRef.current !== null) window.clearTimeout(verifyTimerRef.current);
    },
    [],
  );
  // Leaving the tracker (Back to the plan, or the DEV jumper) must clear the
  // outcome, or re-entering shows the exit card and the user can never verify
  // or skip again. Bugbot finding on PR #194.
  useEffect(() => {
    if (step !== "install-script") {
      setInstallOutcome(null);
      setAgentOpen(false);
    }
  }, [step]);

  // The tracker is the last step now, so its exit lands on /dashboard, where
  // an account with no billing meets the paywall. Say which one is next.
  const {
    hasActiveSubscription,
    hasActiveTrial,
    isLoading: billingLoading,
    refreshSubscription,
  } = useSubscription();
  const hasBilling = hasActiveSubscription || hasActiveTrial;
  const dashboardCtaLabel = billingLoading
    ? "Continue"
    : hasBilling
      ? "Go to dashboard"
      : "Start your 14-day trial";

  // Polls from the moment a property exists: the context panel reports crawl
  // state on You, Goal and Tracker, not only on the plan.
  const generation = useExperimentGeneration(propertyId);

  useEffect(() => {
    document.title = "Get started - Humblytics";
  }, []);

  const handleSendMobileLink = async () => {
    if (!token) return;
    setIsSendingMobileLink(true);
    const continueUrl = `${window.location.origin}${STEP_TO_PATH[step]}${window.location.search}`;
    try {
      await sendMobileSetupLink(continueUrl, token);
      setMobileLinkSent(true);
      track("mobile_setup_link_sent", token, { step });
    } catch {
      // silent
    } finally {
      setIsSendingMobileLink(false);
    }
  };

  const dismissMobileBanner = useCallback(() => {
    setMobileBannerDismissed(true);
    try {
      sessionStorage.setItem(MOBILE_BANNER_DISMISSED_KEY, "1");
    } catch {
      // Best-effort: the banner just reappears next step.
    }
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem("theme") as "light" | "dark" | null;
    setTheme("light");
    return () => {
      if (stored && stored !== "light") setTheme(stored);
    };
  }, []);

  useEffect(() => {
    loadIntercom(user?.email, user?.userId, false);
  }, [user?.email, user?.userId]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setWorkspaceLoadFailed(false);
    getAllWorkspaces(token)
      .then((ws) => {
        if (cancelled) return;
        if (ws?.length) {
          // Hub resume passes ?propertyId= for the site the user opened. Hydrate
          // the loader id from the workspace that actually owns that property, not
          // just the first one — a team member in more than one workspace would
          // otherwise get another owner's script id on owner-script-id lookup.
          const resumePropertyId = resumePropertyIdRef.current;
          const owning = resumePropertyId
            ? ws.find((w) => (w.properties ?? []).some((p) => p.propertyId === resumePropertyId))
            : undefined;
          setWorkspaceId((owning ?? ws[0]).workspaceId);
        } else {
          const name = user?.name ? `${user.name}'s Workspace` : "My Workspace";
          return createWorkspace(name, undefined, token).then((w) => {
            if (!cancelled) setWorkspaceId(w.workspaceId);
          });
        }
      })
      .catch(() => {
        // A swallowed failure here used to strand install-script hydration on an
        // endless spinner (the effect below waits on workspaceId forever). Surface
        // it so the panel can show a retry-able error instead.
        if (!cancelled) setWorkspaceLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [token, user?.name, workspaceAttempt]);

  useEffect(() => {
    if (step !== "install-script" || scriptId || !token) return;

    const fromJwt = pickScriptId(user?.scriptId);
    if (fromJwt) {
      setScriptId(fromJwt);
      setScriptIdLoadFailed(false);
      return;
    }

    if (!workspaceId) {
      // No workspace yet: surface the error only when bootstrap actually failed.
      // Otherwise it's still resolving and setting workspaceId re-runs this
      // effect. A valid workspaceId must always win over a stale failure flag so
      // a later empty/failed reload can't force the error state.
      if (workspaceLoadFailed) setScriptIdLoadFailed(true);
      return;
    }

    let cancelled = false;
    setScriptIdLoadFailed(false);
    getWorkspaceOwnerScriptId(workspaceId, token)
      .then((payload) => {
        if (cancelled) return;
        const id = scriptIdFromOwnerResponse(payload);
        if (id) {
          setScriptId(id);
          setScriptIdLoadFailed(false);
        } else {
          setScriptIdLoadFailed(true);
        }
      })
      .catch(() => {
        if (!cancelled) setScriptIdLoadFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [step, scriptId, token, user?.scriptId, workspaceId, workspaceLoadFailed]);

  const validateDomain = useCallback((input: string): boolean => {
    if (!input.trim()) {
      setDomainError("");
      return false;
    }
    const d = normalizeDomain(input);
    if (d) {
      setDomain(d);
      setDomainError("");
      return true;
    }
    setDomainError("Enter domain without 'www' or 'http://' (e.g., example.com)");
    return false;
  }, []);

  const deriveSiteName = useCallback((d: string): string => {
    if (!d) return "";
    const base = d.replace(/\.[^.]+$/, "").replace(/[-_]/g, " ");
    return (
      base
        .split(" ")
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ") || d
    );
  }, []);

  const handleDomainChange = (v: string) => {
    setDomain(v);
    setDomainError("");
    setUnreachableSite(null);
    setSiteName(
      deriveSiteName(
        normalizeDomain(v) ?? v.replace(/^(https?:\/\/)?(www\.)?/i, "").split("/")[0],
      ),
    );
  };
  const isDomainValid = domain.trim() !== "" && !domainError;
  const hasDomainSoftWarning =
    domain.trim() !== "" && !domainError && /^https?:\/\/|^www\./i.test(domain.trim());

  const enqueueGeneration = useCallback(
    (pId: string, d: string) => {
      if (!token) return;
      const pageUrl = `https://${d.replace(/^(https?:\/\/)?/i, "")}`;
      const goal = selectedGoal;
      stampGenerationPending({
        propertyId: pId,
        domain: d,
        goal,
        siteAddedAt: new Date().toISOString(),
      });
      triggerExperimentGeneration(pId, pageUrl, goal, token)
        .then((res) => {
          stampGenerationPending({
            propertyId: pId,
            domain: d,
            goal,
            siteAddedAt: new Date().toISOString(),
            jobId: res.jobId,
          });
        })
        .catch(() => {
          // Research step falls back to heuristics if the job never appears.
        });
    },
    [token, selectedGoal],
  );

  const persistSiteContext = (pId: string, d: string) => {
    setSiteContext(pId, {
      icp: selectedIcp,
      goal: selectedGoal,
      domain: d,
    });
  };

  const handleWorkModeSelect = (mode: OnboardingWorkMode) => {
    setWorkMode(mode);
    try {
      localStorage.setItem(WORK_MODE_KEY, mode);
    } catch {
      // Best-effort: the choice still drives this session's order.
    }
    track("onboarding_work_mode_selected", token, { work_mode: mode, ...getAcquisitionContext() });
  };

  /** Commits step 01's answer. The server records it; "agent" also grants Free start. */
  const handleWorkModeContinue = () => {
    if (!workMode || !token) return;
    void saveWorkMode(token, workMode === "agent" ? "agent" : "dashboard")
      .then(() => refreshSubscription())
      .catch(() => {
        // Not blocking: the MCP approval grants Free start on its own.
      });
    // Agent accounts finish in their agent; /agent is the placeholder if they come back here.
    if (workMode === "agent") navigate(AGENT_HOME_PATH, { replace: true });
    else setStep("add-site");
  };

  // Signed up from an agent CTA on the marketing site (`path=agent`): the choice
  // was made there, so commit it and skip the question. Platform sign-ups still see it.
  const agentPathCommittedRef = useRef(false);
  useEffect(() => {
    if (step !== "start" || !token || agentPathCommittedRef.current) return;
    if (getSignupPath() !== "agent") return;
    agentPathCommittedRef.current = true;
    setWorkMode("agent");
    try {
      localStorage.setItem(WORK_MODE_KEY, "agent");
    } catch {
      // Best-effort: the server still records the choice below.
    }
    track("onboarding_work_mode_selected", token, { work_mode: "agent", auto: true, ...getAcquisitionContext() });
    void saveWorkMode(token, "agent")
      .then(() => refreshSubscription())
      .catch(() => {
        // Not blocking: the MCP approval grants Free start on its own.
      });
    navigate(AGENT_HOME_PATH, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once per sign-up, on the start step
  }, [step, token]);

  const handleIcpSelect = (icp: OnboardingIcpId) => {
    setSelectedIcp(icp);
    writeOnboardingDraft({ icp });
    track("onboarding_icp_selected", token, { icp, work_mode: workMode, ...getAcquisitionContext() });
    setStep("select-goal");
  };

  const handleGoalSelect = (goalId: Exclude<OnboardingGoalId, "express">) => {
    setSelectedGoal(goalId);
    writeOnboardingDraft({ goal: goalId });
    track("onboarding_goal_selected", token, {
      goal: goalId,
      icp: selectedIcp,
      ...getAcquisitionContext(),
    });
    setStep("campaign");
  };

  const handleCreateSite = async ({ addAnyway = false }: { addAnyway?: boolean } = {}) => {
    if (!isDomainValid || !token || !workspaceId) return;
    // Use the normalized value directly: Enter submits without a blur, so the
    // state can still hold "https://www.Brand.com/pricing" at this point.
    const d = normalizeDomain(domain);
    if (!d) {
      validateDomain(domain);
      return;
    }
    setDomain(d);
    // Back from a later step with the same site: nothing to create. A changed
    // domain falls through and creates the new site rather than keeping the old
    // property under a new label.
    if (propertyId && createdDomain === d) {
      setStep("profile", { propertyId, domain: d });
      return;
    }
    const name = siteName.trim() || deriveSiteName(d);
    setIsCreatingSite(true);
    // A dead or mistyped domain never gets read, so the crawl would hang on
    // "Reading" with no feedback. Check first; a failed check never blocks.
    if (!addAnyway) {
      try {
        const check = await checkSite(d, token);
        if (check && check.reachable === false) {
          setUnreachableSite({ domain: d, status: check.status ?? null });
          track("site_check_unreachable", token, { domain: d, status: check.status ?? null });
          setIsCreatingSite(false);
          return;
        }
      } catch {
        // Check unavailable: add the site as before.
      }
    }
    setUnreachableSite(null);
    try {
      const r = await addCustomSiteToWorkspace(workspaceId, d, name, false, token, true, true);
      const pId = r?.propertyId || r?.property?.propertyId;
      const sId = pickScriptId(r?.scriptId, r?.property?.scriptId);
      if (r?.duplicate) {
        let resolvedId = pId;
        let resolvedScript = sId;
        if (!resolvedId) {
          try {
            const propsData = await getAllProperties(token);
            const match = (propsData?.properties ?? []).find(
              (p: { domain?: string; property_id?: string; propertyId?: string }) =>
                normalizeDomain(p.domain || "") === d,
            );
            if (match) {
              resolvedId = match.property_id || match.propertyId;
              resolvedScript = pickScriptId(resolvedScript, user?.scriptId);
            }
          } catch {
            // Falls through to the error below.
          }
        }
        track("site_added_duplicate", token, { domain: d, goal: selectedGoal, icp: selectedIcp });
        if (!resolvedId) {
          // Navigating on would bounce straight back here via the property guard.
          toast.error(`We couldn't open ${d}`, {
            description: "It may already be on another Humblytics account. Chat with support and we'll sort it out.",
          });
          return;
        }
        setPropertyId(resolvedId);
        setCreatedDomain(d);
        if (resolvedScript) setScriptId(resolvedScript);
        persistSiteContext(resolvedId, d);
        enqueueGeneration(resolvedId, d);
        setStep("profile", { propertyId: resolvedId, domain: d });
        return;
      }
      if (pId) {
        setPropertyId(pId);
        setCreatedDomain(d);
        setScriptId(sId ?? null);
        toast.success("Site added!");
        trackSplitTestSetup("onboarding", "onboarding/add-site", "completed", token);
        track("site_added", token, {
          domain: d,
          workspace_id: workspaceId ?? undefined,
          goal: selectedGoal,
          icp: selectedIcp,
        });
        persistSiteContext(pId, d);
        enqueueGeneration(pId, d);
        setStep("profile", { propertyId: pId, domain: d });
      } else throw new Error("No property ID");
    } catch (error) {
      toast.error(getUserFacingApiError(error, "Failed to add site. Please try again."));
    } finally {
      setIsCreatingSite(false);
    }
  };

  /**
   * The tracker is the wizard's last step, so verifying (or explicitly
   * skipping) ends onboarding. `onboarding_completed` keeps its original firing
   * point: leaving install-script, carrying whether the script was verified.
   */
  /** The side effects of finishing the tracker step. Never deferred. */
  const recordInstall = useCallback(
    (wasVerified: boolean) => {
      setVerified(wasVerified);
      if (propertyId) markGenerationVerified(propertyId, wasVerified);
      track("onboarding_completed", token, {
        verified: wasVerified,
        domain,
        goal: selectedGoal,
        icp: selectedIcp,
      });
    },
    [token, domain, selectedGoal, selectedIcp, propertyId],
  );

  /** Paint the exit card, but only while the tracker step is still on screen. */
  const paintInstallOutcome = useCallback((wasVerified: boolean) => {
    if (stepRef.current === "install-script") {
      setInstallOutcome(wasVerified ? "verified" : "skipped");
    }
  }, []);

  const completeInstall = useCallback(
    (wasVerified: boolean) => {
      recordInstall(wasVerified);
      paintInstallOutcome(wasVerified);
    },
    [recordInstall, paintInstallOutcome],
  );

  /**
   * The research state machine lives on the plan step now. The crawl started on
   * site submit, three steps back, so the plan usually opens on a resolved job
   * and the strip never shows. When it does not, the plan still renders (with
   * heuristic drafts) and the strip reports the wait in place.
   *
   * `onboarding_research_started` therefore fires when the PLAN opens on a
   * pending job, and `_completed` / `_failed` when that job settles or the 20s
   * ceiling passes. A job already resolved on arrival fires none of the three:
   * there was no wait to measure.
   */
  const researchStartedRef = useRef(false);
  const researchSettledRef = useRef(false);
  const planEnteredAt = useRef<number | null>(null);
  const [planTick, setPlanTick] = useState(0);
  const [researchCapped, setResearchCapped] = useState(false);

  const settleResearch = useCallback(
    (outcome: "ready" | "timeout" | "failed", extra?: Record<string, unknown>) => {
      if (!researchStartedRef.current || researchSettledRef.current) return;
      researchSettledRef.current = true;
      if (outcome === "failed") {
        track("onboarding_research_failed", token, extra);
      } else {
        track("onboarding_research_completed", token, extra);
      }
    },
    [token],
  );

  const generationPending =
    !generation.isReady && !generation.isFailed && !generation.isError;

  useEffect(() => {
    if (step !== "campaign") {
      researchStartedRef.current = false;
      researchSettledRef.current = false;
      planEnteredAt.current = null;
      setResearchCapped(false);
      return;
    }
    if (planEnteredAt.current === null) planEnteredAt.current = Date.now();
    if (!researchStartedRef.current && generationPending) {
      researchStartedRef.current = true;
      track("onboarding_research_started", token, {
        domain,
        goal: selectedGoal,
        icp: selectedIcp,
        propertyId,
      });
    }
    const interval = window.setInterval(() => setPlanTick((n) => n + 1), 1000);
    return () => window.clearInterval(interval);
  }, [step, token, domain, selectedGoal, selectedIcp, propertyId, generationPending]);

  // The 20s ceiling is the strip's, not a gate: past it we stop claiming the
  // crawl is still coming and let the heuristic drafts stand.
  useEffect(() => {
    if (step !== "campaign" || !researchStartedRef.current || researchSettledRef.current) return;
    const cap = window.setTimeout(() => {
      setResearchCapped(true);
      settleResearch("timeout", { outcome: "timeout", domain, goal: selectedGoal });
    }, RESEARCH_CAP_MS);
    return () => window.clearTimeout(cap);
  }, [step, planTick, domain, selectedGoal, settleResearch]);

  useEffect(() => {
    if (step !== "campaign") return;
    if (generation.isReady) {
      settleResearch("ready", {
        outcome: "ready",
        rec_count: generation.job?.result?.analysis?.recommendations?.length ?? 0,
      });
    } else if (generation.isFailed || generation.isError) {
      settleResearch("failed", { reason: generation.job?.error?.code ?? "error" });
    }
  }, [step, generation.isReady, generation.isFailed, generation.isError, generation.job, settleResearch]);

  /** What the plan's strip reports. `pending` is the only state that shows one. */
  const researchState: "pending" | "done" | "failed" = generation.isReady
    ? "done"
    : generation.isFailed || generation.isError || researchCapped
      ? "failed"
      : "pending";

  const secondsOnStep = useMemo(() => {
    void planTick;
    if (step !== "campaign" || planEnteredAt.current === null) return 0;
    return Math.floor((Date.now() - planEnteredAt.current) / 1000);
  }, [planTick, step]);

  const campaignRecs = useMemo(() => {
    const fromJob = (generation.job?.result?.analysis?.recommendations ?? [])
      .slice()
      .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))
      .slice(0, 2);
    if (fromJob.length) return { recs: fromJob, heuristic: false };
    return { recs: heuristicRecommendations(selectedGoal, detectedPlatform), heuristic: true };
  }, [generation.job, selectedGoal, detectedPlatform]);

  const planAnalysis = generation.job?.result?.analysis ?? null;

  /** The plan opens on the reading view (Paper 03a) while the crawl is pending. */
  const showReadingView = step === "campaign" && researchState === "pending";
  // The reading view's browser frame shows the captured page as soon as one
  // exists. Same query key as the plan cards, so the two share one poll.
  const readingSnapshotUrl = useOnboardingSnapshot({
    propertyId,
    token,
    domain,
    enabled: showReadingView,
  });

  /**
   * The plan owns the site-snapshot poll and reports its URL up, so the
   * `screenshot_state` this page records matches what the cards show. One
   * derivation, shared with the plan through `screenshotStateFor`.
   */
  const [planSnapshotUrl, setPlanSnapshotUrl] = useState<string | null>(null);
  const screenshotState = useMemo(
    () =>
      screenshotStateFor(
        {
          firecrawlUrl: generation.job?.result?.preview?.beforeUrl ?? null,
          snapshotUrl: planSnapshotUrl ?? readingSnapshotUrl,
        },
        planAnalysis,
      ),
    [generation.job, planSnapshotUrl, readingSnapshotUrl, planAnalysis],
  );

  /**
   * Viewed fires on open, then once more if the job resolves in place, so the
   * `screenshot_state` we finally showed is recorded rather than the one we
   * opened with.
   */
  const campaignViewedRef = useRef(false);
  const campaignRefiredRef = useRef(false);
  useEffect(() => {
    if (step !== "campaign") return;
    const payload = {
      rec_count: campaignRecs.recs.length,
      heuristic: campaignRecs.heuristic,
      goal: selectedGoal,
      icp: selectedIcp,
      screenshot_state: screenshotState,
    };
    if (!campaignViewedRef.current) {
      campaignViewedRef.current = true;
      track("onboarding_plan_viewed", token, payload);
      return;
    }
    if (!campaignRefiredRef.current && generation.isReady) {
      campaignRefiredRef.current = true;
      track("onboarding_plan_viewed", token, { ...payload, upgraded_in_place: true });
    }
  }, [step, token, campaignRecs, selectedGoal, selectedIcp, screenshotState, generation.isReady]);

  const leaveToDashboard = useCallback(() => {
    navigate("/dashboard", { replace: true });
  }, [navigate]);

  /**
   * The wizard's exit (Paper 05 Revenue): once the tag is verified or skipped,
   * the install step becomes the connectors offer. Connecting is optional and
   * both exits land on /dashboard, where an account with no billing meets the
   * paywall, so the primary label names it.
   */
  const showAgentView = step === "install-script" && installOutcome !== null && agentOpen;
  const showRevenueView = step === "install-script" && installOutcome !== null && !agentOpen;
  const openAgentStep = useCallback(
    (from: "continue" | "later") => {
      track("onboarding_agent_step_viewed", token, { from, verified });
      setAgentOpen(true);
    },
    [token, verified],
  );
  const revenueConnectors = useRevenueConnections(propertyId, token, showRevenueView);
  const [connectingId, setConnectingId] = useState<RevenueConnectorId | null>(null);
  const handleRevenueConnect = useCallback(
    async (id: RevenueConnectorId) => {
      if (!propertyId || !token) return;
      track("onboarding_revenue_connect_clicked", token, { connector: id, verified });
      if (id === "stripe") {
        navigate(`/p/${generateSlugId(siteName || domain, propertyId)}/connectors/stripe`);
        return;
      }
      setConnectingId(id);
      try {
        const { authUrl } =
          id === "meta"
            ? await initiateMetaOAuth(propertyId, token)
            : await initiateGoogleAdsOAuth(propertyId, token);
        // App path: the wizard is done by now, so the callback returns to the hub.
        // Agent path: goal and tests still follow, so it returns to the goal step.
        sessionStorage.setItem(
          "oauth_return_path",
          isAgentPath ? `/onboarding/select-goal?propertyId=${encodeURIComponent(propertyId)}` : "/dashboard",
        );
        logConnectorEvent("oauth_started", { propertyId }, { connectorId: id });
        window.location.href = authUrl;
      } catch (error) {
        reportConnectorFailure("oauth_start", error, { propertyId }, { connectorId: id });
        toast.error("Couldn't open the connection. Try again from Connectors.");
        setConnectingId(null);
      }
    },
    [propertyId, token, verified, navigate, siteName, domain, isAgentPath],
  );

  /**
   * Dismissing the call overlay returns to the plan. It used to exit to the
   * dashboard, which would now skip the tracker step entirely.
   */
  // Set when the user picks a time. Booking and dismissing are exclusive funnel
  // branches, so a booked user closing the (still-open) overlay via X / backdrop
  // / Escape must not also fire `onboarding_call_dismissed`.
  const bookedCallRef = useRef(false);
  const finishCallOverlay = useCallback(
    (dismissed: boolean) => {
      if (dismissed) {
        track("onboarding_call_dismissed", token, { goal: selectedGoal, icp: selectedIcp });
      }
      setCallOpen(false);
    },
    [token, selectedGoal, selectedIcp],
  );

  const openCallModal = () => {
    bookedCallRef.current = false;
    setCallOpen(true);
    track("onboarding_call_shown", token, { goal: selectedGoal, icp: selectedIcp });
  };

  const handlePlanContinue = useCallback(
    (meta: PlanCtaMeta) => {
      track("onboarding_plan_cta_clicked", token, {
        heuristic: meta.heuristic,
        screenshot_state: meta.screenshot_state,
        seconds_on_step: meta.seconds_on_step ?? secondsOnStep,
      });
      // Agent path: the script went in before the goal, so the plan is the last step.
      if (isAgentPath) {
        leaveToDashboard();
        return;
      }
      setStep("install-script");
    },
    [token, secondsOnStep, setStep, isAgentPath, leaveToDashboard],
  );

  /**
   * Agent path: once the script is verified (or skipped), the connectors offer
   * shows (Paper 04 Agent flow, "Path · Connect your data"), then the goal. The
   * agent is already connected, so the agent view never opens on this path.
   */
  const leaveRevenueView = useCallback(() => {
    if (isAgentPath) {
      setStep("select-goal");
      return;
    }
    openAgentStep("continue");
  }, [isAgentPath, setStep, openAgentStep]);

  /**
   * Leaving the tab or reloading mid-wizard. `track` posts with `keepalive`,
   * which survives pagehide; completing the wizard unmounts this listener
   * before any pagehide can fire, so a finished run never reports as abandoned.
   */
  const stepEnteredAtRef = useRef(Date.now());
  useEffect(() => {
    stepEnteredAtRef.current = Date.now();
  }, [step]);
  useEffect(() => {
    const handler = () => {
      track("onboarding_step_abandoned", token, {
        step: stepRef.current,
        seconds_on_step: Math.floor((Date.now() - stepEnteredAtRef.current) / 1000),
      });
    };
    window.addEventListener("pagehide", handler);
    return () => window.removeEventListener("pagehide", handler);
  }, [token]);

  useEffect(() => {
    if (!token) {
      navigate("/auth", { replace: true });
      return;
    }
    // Everything after the site step needs a property. A direct hit without one
    // goes back to add-site rather than rendering an empty wizard.
    if (REQUIRES_PROPERTY.includes(step) && !propertyId) {
      navigate(STEP_TO_PATH["add-site"], { replace: true });
    }
  }, [token, step, propertyId, navigate]);

  useEffect(() => {
    if (step !== "add-site" || !!propertyId) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [step, propertyId]);

  if (!token) return null;
  if (REQUIRES_PROPERTY.includes(step) && !propertyId) return null;

  const meta = STEP_META[step];
  const evidenceStep = STEP_EVIDENCE[step];
  // First step only, then a one-line footer link. It used to sit on every step.
  const showMobileBanner = isMobile && step === "add-site" && !mobileBannerDismissed;
  const showMobileFooterLink = isMobile && !showMobileBanner;
  // The stepper: every completed step shows what was answered, with the live
  // facts (crawl, tracking) on the Website segment. Replaces the step rail and
  // the side card (founder, 2026-09-10); left-to-right on the team's call.
  const firstHitAgo = formatAgo(firstHitAt, agoTick);
  const websiteStatus: OnboardingStepperStep["status"] = verified
    ? { text: firstHitAgo ? `Tracking live · first pageview ${firstHitAgo}` : "Tracking live", tone: "teal" }
    : researchState === "pending"
      ? { text: "Reading the page", tone: "muted", pulse: true }
      : researchState === "done"
        ? { text: "Page read", tone: "teal" }
        : { text: "Couldn't read the page", tone: "muted" };
  const allSteps: OnboardingStepperStep[] = [
    {
      id: "add-site",
      icon: "website",
      label: "Website",
      value: domain || undefined,
      status: propertyId ? websiteStatus : undefined,
      onEdit: () => setStep("add-site"),
    },
    {
      id: "profile",
      icon: "you",
      label: "You",
      value: selectedIcp ? ONBOARDING_ICP_LABELS[selectedIcp] : undefined,
      onEdit: () => setStep("profile"),
    },
    {
      id: "select-goal",
      icon: "goal",
      label: "Goal",
      value: selectedGoal ? ONBOARDING_GOAL_LABELS[selectedGoal] : undefined,
      onEdit: () => setStep("select-goal"),
    },
    {
      id: "campaign",
      icon: "tests",
      label: "Tests",
      value: `${campaignRecs.recs.length} drafted`,
      onEdit: () => setStep("campaign"),
    },
    {
      id: "install-script",
      icon: "script",
      label: "Script",
      value: installOutcome === "skipped" ? "Skipped" : "Live",
    },
    {
      id: "connect-agent",
      icon: "agent",
      label: "Agent",
      value: isAgentPath && stepOrder.indexOf(step) > stepOrder.indexOf("connect-agent") ? "Connected" : undefined,
    },
  ];
  // Same six segments on both paths; only the order changes (decision 18).
  const segmentOrder: string[] = isAgentPath
    ? AGENT_STEP_ORDER
    : [...STEP_ORDER, "connect-agent"];
  const receiptSteps = segmentOrder
    .map((id) => allSteps.find((s) => s.id === id))
    .filter((s): s is OnboardingStepperStep => Boolean(s));
  const stepperCurrentId = showAgentView ? "connect-agent" : step;

  return (
    <div
      className="h-svh max-w-[100vw] bg-background flex overflow-hidden"
      // Local action token: the app-wide --primary label measures 4.06:1 on
      // white. This one measures above 4.5:1 and is scoped to the wizard.
      style={{ "--onboarding-action": "oklch(0.54 0.21 42)" } as CSSProperties}
    >
      <div className="relative flex min-w-0 flex-1 flex-col">
        <div className="relative flex h-full min-w-0 w-full flex-col">
          <div className="min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-6 pb-8 pt-5">
            <main className="mx-auto w-full min-w-0 max-w-[960px]">
              <div className="mb-3 flex items-center">
                <Link to="/" className="flex items-center gap-2">
                  <img src={HumblyticsLogo} alt="" className="h-6 w-auto" />
                  <span className="text-sm font-medium tracking-tight">Humblytics</span>
                </Link>
              </div>

              {showMobileBanner && (
                <div className="mb-4 rounded-lg border border-warning/40 bg-warning-tint px-4 py-3 dark:bg-warning-tint">
                  <div className="flex items-start gap-3">
                    <HugeiconsIcon icon={SmartPhone01Icon} size={16} className="mt-0.5 shrink-0 text-warning" />
                    <div className="min-w-0 flex-1">
                      <p className="mb-0.5 text-xs font-medium text-warning dark:text-warning">Looks like you're on mobile</p>
                      <p className="mb-2 text-[11px] leading-snug text-warning/80 dark:text-warning/70">
                        Setup is easier on a desktop. Send yourself a link to continue later.
                      </p>
                      {mobileLinkSent ? (
                        <p className="flex items-center gap-1.5 text-[11px] font-medium text-warning dark:text-warning">
                          <HugeiconsIcon icon={Tick01Icon} size={11} className="text-success" /> Link sent. Check your inbox.
                        </p>
                      ) : (
                        <button
                          type="button"
                          onClick={handleSendMobileLink}
                          disabled={isSendingMobileLink}
                          className="flex min-h-6 items-center gap-1.5 text-[11px] font-medium text-warning underline underline-offset-2 hover:no-underline disabled:opacity-60 dark:text-warning"
                        >
                          {isSendingMobileLink ? (
                            <><HugeiconsIcon icon={Loading01Icon} size={11} className="animate-spin motion-reduce:animate-none" /> Sending...</>
                          ) : (
                            <><HugeiconsIcon icon={Mail01Icon} size={11} /> Send me a link</>
                          )}
                        </button>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={dismissMobileBanner}
                      aria-label="Dismiss mobile setup notice"
                      className="-mr-1 -mt-1 flex size-6 shrink-0 items-center justify-center rounded-md text-warning/70 transition-colors hover:bg-warning/10 hover:text-warning dark:text-warning/70 dark:hover:text-warning"
                    >
                      <HugeiconsIcon icon={Cancel01Icon} size={14} />
                    </button>
                  </div>
                </div>
              )}

              {step !== "start" && !isAgentPath ? (
                <OnboardingStepper steps={receiptSteps} currentId={stepperCurrentId} className="mb-6" />
              ) : null}

              {showReadingView ? (
                <OnboardingReadingSite
                  domain={domain || "your site"}
                  steps={readingSteps({
                    domain: domain || "your site",
                    goalNoun: goalNoun(selectedGoal),
                    jobStatus: generation.job?.status ?? null,
                    hasSnapshot: Boolean(readingSnapshotUrl),
                  })}
                  snapshotUrl={readingSnapshotUrl}
                  continueLabel={isAgentPath ? "Go to Home" : "Install to run these"}
                  onContinue={() =>
                    handlePlanContinue({
                      heuristic: campaignRecs.heuristic,
                      screenshot_state: screenshotState,
                      seconds_on_step: secondsOnStep,
                    })
                  }
                />
              ) : showRevenueView ? (
                <OnboardingRevenueStep
                  connectors={revenueConnectors}
                  connectingId={connectingId}
                  onConnect={handleRevenueConnect}
                  continueLabel={isAgentPath ? "Next: pick your goal" : "Continue"}
                  onContinue={leaveRevenueView}
                  onLater={() => {
                    track("onboarding_revenue_later_clicked", token, { verified, work_mode: isAgentPath ? "agent" : "app" });
                    if (isAgentPath) setStep("select-goal");
                    else openAgentStep("later");
                  }}
                />
              ) : step === "connect-agent" ? (
                <OnboardingAgentStep
                  token={token}
                  installNext
                  dashboardLabel="Continue"
                  onPromptCopied={(client) => track("onboarding_agent_prompt_copied", token, { client, work_mode: "agent" })}
                  onFinish={({ connected, client }) => {
                    track(connected ? "onboarding_agent_connected" : "onboarding_agent_skipped", token, {
                      client,
                      work_mode: "agent",
                    });
                    if (connected) {
                      navigate(AGENT_HOME_PATH, { replace: true });
                      return;
                    }
                    // "Set it up in the app instead": switch to the app path.
                    handleWorkModeSelect("app");
                    setStep("add-site");
                  }}
                />
              ) : showAgentView ? (
                <OnboardingAgentStep
                  token={token}
                  dashboardLabel={billingLoading || hasBilling ? "Go to Home" : dashboardCtaLabel}
                  onPromptCopied={(client) => track("onboarding_agent_prompt_copied", token, { client })}
                  onFinish={({ connected, client }) => {
                    track(connected ? "onboarding_agent_connected" : "onboarding_agent_skipped", token, { client, verified });
                    leaveToDashboard();
                  }}
                />
              ) : (
              /* The plan gets a second column at lg: the examples fill the crawl wait and stay put when the tests land. */
              <div className={cn("grid gap-8", step === "campaign" && "lg:grid-cols-[minmax(0,1fr)_368px]")}>
              <div className={cn("min-w-0", step !== "campaign" && "max-w-[640px]")}>
              <div className="mb-4 space-y-1">
                <h1 className="text-xl font-medium leading-tight tracking-tight text-foreground">{meta.title}</h1>
                <p className="max-w-md text-xs text-muted-foreground">{meta.description}</p>
                {evidenceStep ? <OnboardingEvidence step={evidenceStep} className="max-w-md pt-0.5" /> : null}
              </div>
              <Card className="border-border/30">
                <CardContent className={cn(step === "install-script" ? "p-4" : "p-6")}>
                  <AnimatePresence mode="wait" initial={!reduceMotion}>
                    <motion.div
                      key={step}
                      initial={reduceMotion ? false : { opacity: 0, x: -16 }}
                      animate={reduceMotion ? { opacity: 1, x: 0 } : { opacity: 1, x: 0 }}
                      exit={reduceMotion ? { opacity: 1 } : { opacity: 0, x: 16 }}
                      transition={{ duration: reduceMotion ? 0 : 0.15 }}
                    >
                      {step === "start" && (
                        <div className="space-y-6">
                          <OnboardingWorkModeChoice value={workMode} onChange={handleWorkModeSelect} />
                          <Button
                            type="button"
                            disabled={!workMode}
                            onClick={handleWorkModeContinue}
                            className="h-11 w-full rounded-lg bg-[var(--onboarding-action)] text-sm font-medium text-white hover:bg-[var(--onboarding-action)] hover:opacity-90"
                          >
                            Continue
                          </Button>
                        </div>
                      )}

                      {step === "profile" && (
                        <div role="radiogroup" aria-label="Who are you?" className="space-y-3">
                          {ICP_IDS.map((id) => (
                            <OnboardingChoiceCard
                              key={id}
                              semantics="radio"
                              selected={selectedIcp === id}
                              label={ONBOARDING_ICP_LABELS[id]}
                              description={ONBOARDING_ICP_DESCRIPTIONS[id]}
                              art={ICP_ART[id]}
                              onSelect={() => handleIcpSelect(id)}
                            />
                          ))}
                        </div>
                      )}

                      {step === "select-goal" && (
                        <div role="radiogroup" aria-label="What do you want more of?" className="space-y-3">
                          {SELECTABLE_ONBOARDING_GOALS.map((id) => (
                            <OnboardingChoiceCard
                              key={id}
                              semantics="radio"
                              selected={selectedGoal === id}
                              label={ONBOARDING_GOAL_LABELS[id]}
                              description={ONBOARDING_GOAL_DESCRIPTIONS[id]}
                              art={GOAL_ART[id]}
                              onSelect={() => handleGoalSelect(id)}
                            />
                          ))}
                        </div>
                      )}

                      {step === "add-site" && (
                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            void handleCreateSite();
                          }}
                          className="space-y-4"
                        >
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-1.5">
                              <Label htmlFor="domain" className="text-xs">Your site</Label>
                              <span className="text-[11px] text-danger">*</span>
                            </div>
                            <InputGroup
                              className={cn(
                                "h-9",
                                hasDomainSoftWarning && "border-warning/60 dark:border-warning/50",
                                domainError && "border-destructive ring-[2px] ring-destructive/20",
                              )}
                            >
                              <InputGroupAddon>
                                <InputGroupText>https://</InputGroupText>
                              </InputGroupAddon>
                              <InputGroupInput
                                id="domain"
                                placeholder="yourbrand.com"
                                value={domain}
                                onChange={(e) => handleDomainChange(e.target.value)}
                                onBlur={() => domain && validateDomain(domain)}
                                autoFocus
                                autoCapitalize="none"
                                autoCorrect="off"
                                autoComplete="off"
                                aria-invalid={!!domainError}
                              />
                              {domain && !domainError && !hasDomainSoftWarning && (
                                <InputGroupAddon align="inline-end">
                                  <HugeiconsIcon icon={Tick01Icon} size={14} className="text-success dark:text-success" />
                                </InputGroupAddon>
                              )}
                              {hasDomainSoftWarning && (
                                <InputGroupAddon align="inline-end">
                                  <HugeiconsIcon icon={Alert01Icon} size={14} className="text-warning" />
                                </InputGroupAddon>
                              )}
                            </InputGroup>
                            {domainError ? (
                              <p className="text-[11px] text-danger">{domainError}</p>
                            ) : unreachableSite && unreachableSite.domain === normalizeDomain(domain) ? (
                              <div role="alert" className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-warning-tint px-3 py-2">
                                <p className="text-[11px] text-warning">
                                  {unreachableSite.status === 404 || unreachableSite.status === 410
                                    ? `${unreachableSite.domain} returned a ${unreachableSite.status}. Check the address.`
                                    : unreachableSite.status
                                      ? `${unreachableSite.domain} returned an error (${unreachableSite.status}).`
                                      : `${unreachableSite.domain} didn't respond. Check the address.`}
                                </p>
                                <button
                                  type="button"
                                  onClick={() => {
                                    track("site_check_add_anyway", token, { domain: unreachableSite.domain, status: unreachableSite.status });
                                    void handleCreateSite({ addAnyway: true });
                                  }}
                                  className="min-h-6 text-[11px] font-medium text-warning underline underline-offset-2 hover:no-underline"
                                >
                                  Add it anyway
                                </button>
                              </div>
                            ) : hasDomainSoftWarning ? (
                              <p className="text-[11px] text-warning dark:text-warning">
                                No need for "www" or "https://". We'll strip that automatically.
                              </p>
                            ) : marketingDomain && domain === marketingDomain ? (
                              <p className="text-[11px] text-muted-foreground/60">Pre-filled from your signup, change it if needed.</p>
                            ) : null}
                          </div>
                          {workspaceLoadFailed && !workspaceId ? (
                            <p role="alert" className="text-[11px] text-danger">
                              Couldn't set up your workspace.{" "}
                              <button
                                type="button"
                                onClick={() => setWorkspaceAttempt((n) => n + 1)}
                                className="min-h-6 font-medium underline underline-offset-2 hover:no-underline"
                              >
                                Try again
                              </button>
                            </p>
                          ) : null}
                          <Button
                            type="submit"
                            disabled={!isDomainValid || isCreatingSite || !workspaceId}
                            className="h-9 w-full gap-2 bg-[var(--onboarding-action)] text-white hover:bg-[var(--onboarding-action)] hover:opacity-90"
                          >
                            {isCreatingSite ? (
                              <><HugeiconsIcon icon={Loading01Icon} size={14} className="animate-spin motion-reduce:animate-none" /> Adding...</>
                            ) : (
                              <>Continue <HugeiconsIcon icon={ArrowRight01Icon} size={14} /></>
                            )}
                          </Button>
                        </form>
                      )}

                      {step === "install-script" && !installOutcome && token && (
                        scriptId ? (
                        <InstallScriptPanel
                          domain={domain}
                          scriptId={scriptId}
                          token={token}
                          propertyId={propertyId || undefined}
                          allowContinueUnverified
                          draftedTestCount={campaignRecs.recs.length}
                          onFirstHit={setFirstHitAt}
                          onMethodChange={(method) =>
                            track("onboarding_install_method_selected", token, { method, goal: selectedGoal })
                          }
                          onCopied={(platform, method) =>
                            track("onboarding_script_copied", token, { platform, install_method: method, goal: selectedGoal })
                          }
                          onIdCopied={() => track("onboarding_id_copied", token, { goal: selectedGoal })}
                          onInstructionsExpanded={(platform) =>
                            track("onboarding_install_instructions_expanded", token, { platform, goal: selectedGoal })
                          }
                          onPlatformDetected={(platform, source) => {
                            track("onboarding_platform_detected", token, { platform, source, goal: selectedGoal });
                            setDetectedPlatform(platform as PlatformId);
                            if (propertyId) setSiteContextPlatform(propertyId, platform, domain);
                          }}
                          onVerified={(attempts) => {
                            trackSplitTestSetup("onboarding", "onboarding/install-script", "completed", token);
                            track("tracking_script_verified", token, { attempts, goal: selectedGoal });
                            // Record now; only the exit-card paint waits for the beat.
                            recordInstall(true);
                            verifyTimerRef.current = window.setTimeout(() => {
                              verifyTimerRef.current = null;
                              paintInstallOutcome(true);
                            }, 1000);
                          }}
                          onVerificationFailed={(attempt) =>
                            track("verification_failed", token, { attempt_count: attempt, goal: selectedGoal })
                          }
                          agentFirst={isAgentPath}
                          onContinueUnverified={(failCount) => {
                            trackSplitTestSetup("onboarding", "onboarding/install-script", "abandoned", token);
                            track("onboarding_verification_skipped", token, { fail_count: failCount, goal: selectedGoal });
                            completeInstall(false);
                          }}
                        />
                        ) : (
                          <div className="rounded-lg border border-border/30 bg-muted/20 px-3 py-4 text-sm text-muted-foreground">
                            {scriptIdLoadFailed ? (
                              "Couldn't load tracking id. Refresh and try again."
                            ) : (
                              <span className="inline-flex items-center gap-1.5">
                                <HugeiconsIcon icon={Loading01Icon} size={12} className="animate-spin" />
                                Loading your tracking id…
                              </span>
                            )}
                          </div>
                        )
                      )}

                      {step === "campaign" && (
                        <OnboardingPlanRecs
                          recs={campaignRecs.recs}
                          goal={selectedGoal}
                          heuristic={campaignRecs.heuristic}
                          continueLabel={isAgentPath ? "Go to Home" : "Install to run these"}
                          propertyId={propertyId ?? undefined}
                          token={token}
                          domain={domain || undefined}
                          controlImageUrl={generation.job?.result?.preview?.beforeUrl || null}
                          variantImageUrl={
                            campaignRecs.heuristic
                              ? null
                              : generation.job?.result?.preview?.afterUrl || null
                          }
                          analysis={planAnalysis}
                          researchState={researchState}
                          secondsOnStep={secondsOnStep}
                          onSnapshotUrlChange={setPlanSnapshotUrl}
                          examplesClassName="lg:hidden"
                          onContinue={handlePlanContinue}
                          onBookCall={openCallModal}
                        />
                      )}
                    </motion.div>
                  </AnimatePresence>
                </CardContent>
              </Card>
              </div>
              {step === "campaign" ? (
                <aside aria-label="What others tested" className="hidden min-w-0 lg:block">
                  <OnboardingExampleTests
                    goal={selectedGoal}
                    pending={researchState === "pending"}
                    headingId="onboarding-examples-heading-side"
                    className="sticky top-2"
                  />
                </aside>
              ) : null}
              </div>
              )}
            </main>
          </div>

          <footer className="shrink-0 border-t border-border/30 bg-background/80 px-6 py-3 backdrop-blur-sm">
            {showMobileFooterLink && (
              <p className="mb-2 text-xs text-muted-foreground">
                {mobileLinkSent ? (
                  <span className="inline-flex items-center gap-1.5">
                    <HugeiconsIcon icon={Tick01Icon} size={11} className="text-success" /> Link sent. Check your
                    inbox.
                  </span>
                ) : (
                  <>
                    On mobile?{" "}
                    <button
                      type="button"
                      onClick={handleSendMobileLink}
                      disabled={isSendingMobileLink}
                      className="min-h-6 text-[var(--onboarding-action)] hover:underline disabled:opacity-60"
                    >
                      {isSendingMobileLink ? "Sending..." : "Send yourself a link"}
                    </button>
                  </>
                )}
              </p>
            )}
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">
                Need help?{" "}
                <button type="button" onClick={showIntercom} className="min-h-6 text-[var(--onboarding-action)] hover:underline">Chat with support</button>
                {" "}or{" "}
                <a href="https://docs.humblytics.com" target="_blank" rel="noopener noreferrer" className="min-h-6 text-[var(--onboarding-action)] hover:underline">browse docs</a>.
              </p>
              {/* The text links say the same thing; below md these buttons sit under the Intercom launcher. */}
              <div className="hidden shrink-0 gap-1.5 md:flex">
                <Button asChild variant="ghost" size="sm" className="h-7 gap-1 px-2.5 text-xs">
                  <a href="https://docs.humblytics.com" target="_blank" rel="noopener noreferrer">
                    <HugeiconsIcon icon={FileAttachmentIcon} size={12} /> Docs
                  </a>
                </Button>
                <Button variant="ghost" size="sm" className="h-7 gap-1 px-2.5 text-xs" onClick={showIntercom}>
                  <HugeiconsIcon icon={Comment02Icon} size={12} /> Support
                </Button>
              </div>
            </div>
          </footer>
        </div>
      </div>

      <OnboardingCallModal
        open={callOpen}
        onOpenChange={(open) => {
          if (open) {
            setCallOpen(true);
            return;
          }
          // Closing after booking is not a dismissal — it's the exit path a
          // booked user takes to /dashboard, and dismissed is a rival branch.
          finishCallOverlay(!bookedCallRef.current);
        }}
        onBook={() => {
          bookedCallRef.current = true;
          track("onboarding_call_clicked", token, { goal: selectedGoal, icp: selectedIcp, verified });
        }}
        onDismiss={() => finishCallOverlay(true)}
      />

      {import.meta.env.DEV && (
      <div className="fixed bottom-4 right-4 z-50">
        <AnimatePresence>
          {devPanelOpen && (
            <motion.div
              initial={{ opacity: 0, y: 8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="mb-2 min-w-[200px] rounded-lg border border-border/60 bg-background p-3 shadow-lg"
            >
              <p className="mb-2 text-xs font-semibold text-foreground/80">Dev nav</p>
              <div className="space-y-1">
                {stepOrder.map((id, index) => (
                  <button
                    key={id}
                    onClick={() => {
                      setDevPanelOpen(false);
                      if (id === "install-script") {
                        setInstallOutcome(null);
                        if (!scriptId) {
                          setPropertyId((prev) => prev ?? "dev-preview");
                          setScriptId("dev-preview");
                        }
                      }
                      // Every step past the site step needs one, or the guard
                      // bounces the jumper straight back to add-site.
                      if (REQUIRES_PROPERTY.includes(id) && !propertyId) {
                        setPropertyId("dev-preview");
                      }
                      setStep(id);
                    }}
                    className={cn(
                      "w-full rounded-md px-2.5 py-1.5 text-left text-xs transition-colors hover:bg-muted",
                      step === id && "bg-muted font-medium text-foreground",
                    )}
                  >
                    {index + 1}. {STEP_SHORT_LABELS[id]}
                  </button>
                ))}
                <button
                  onClick={() => { setDevPanelOpen(false); navigate("/dashboard"); }}
                  className="w-full rounded-md px-2.5 py-1.5 text-left text-xs transition-colors hover:bg-muted"
                >
                  → Dashboard
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <button
          onClick={() => setDevPanelOpen(!devPanelOpen)}
          className="flex min-h-6 items-center gap-1.5 rounded-full border border-border/60 bg-foreground/10 px-3 py-1.5 font-mono text-[11px] text-foreground shadow-sm transition-colors hover:bg-foreground/15"
        >
          DEV{" "}
          <HugeiconsIcon icon={ArrowDown01Icon} size={10} className={cn("transition-transform", devPanelOpen && "rotate-180")} />
        </button>
      </div>
      )}
    </div>
  );
}
